"use client"

import { useCallback } from "react"
import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/auth/use-auth"

export interface Voice {
  id: string
  name: string
  description?: string
  languages: string[]
  previewUrl?: string
  category?: string
  labels?: Record<string, string>
}

export const voicesQueryKey = (tenantId: string) =>
  ["voice", tenantId, "voices"] as const

export function useVoices() {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const tenantId =
    user?.impersonated_tenant_id ?? user?.tenant_id ?? "unauthenticated"
  const query = useQuery({
    queryKey: voicesQueryKey(tenantId),
    enabled: isAuthenticated,
    queryFn: async (): Promise<Voice[]> => {
      const response = await fetchWithAuth("/api/voice/voices")
      const data = (await response.json()) as {
        voices?: Voice[]
        error?: string
      }
      if (!response.ok) {
        throw new Error(data.error ?? "Failed to load voices")
      }
      return data.voices ?? []
    },
  })
  const voices = query.data ?? []
  const refetchVoices = query.refetch

  const getVoice = (id: string) =>
    voices.find((voice) => voice.id === id)

  const refreshVoices = useCallback(
    async () => {
      await refetchVoices()
    },
    [refetchVoices],
  )

  return {
    voices,
    isLoading: isAuthenticated ? query.isPending : false,
    error: query.error instanceof Error ? query.error.message : null,
    getVoice,
    refreshVoices,
  }
}
